// We have to do this because if we have a mistmatch between the imports of ComponentFixture from
// angular between this library and any dependent code, TypeScript throws a hissy fit
interface IComponentFixture {
    nativeElement: HTMLElement;
    detectChanges: () => void;
}

type QuerySelectorOrElement<T extends Element = HTMLElement> = string | T;

export class UnitTestHelpers<F extends IComponentFixture> {
    constructor(private fixture: F) {}

    querySelector<T extends Element>(selector: string): T {
        const hostElement: HTMLElement = this.fixture.nativeElement;
        return hostElement.querySelector(selector);
    }
    querySelectorAll<T extends Element>(selector: string): NodeListOf<T> {
        const hostElement: HTMLElement = this.fixture.nativeElement;
        return hostElement.querySelectorAll(selector);
    }

    /**
     * Enters text inside an input element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param text The text to enter in the input element.
     */
    enterInputText(
        querySelectorOrElement: string | HTMLInputElement,
        text: string
    ) {
        const inputElement = this.querySelectorOrElement<HTMLInputElement>(
            querySelectorOrElement
        );
        // simulate user entering a new name into the input box
        inputElement.value = text;

        // Dispatch a DOM event so that Angular learns of input value change.
        // In older browsers, such as IE, you might need a CustomEvent instead. See
        // https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill
        inputElement.dispatchEvent(new Event('input'));

        // Tell Angular to update the display binding through the title pipe
        this.fixture.detectChanges();
    }

    clickSelectElementOption(
        querySelectorOrElement: string | HTMLSelectElement,
        optionIndex: number
    ) {
        const selectElement = this.querySelectorOrElement<HTMLSelectElement>(
            querySelectorOrElement
        );
        const clickedOption = selectElement.options[optionIndex];
        if (!clickedOption) {
            throw new Error('Invalid option index.');
        }
        selectElement.value = clickedOption.value;
        selectElement.dispatchEvent(new Event('change'));

        this.fixture.detectChanges();
    }

    /**
     * Triggers an event on an element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param eventName The name of the event to trigger on the element.
     */
    triggerEvent(
        querySelectorOrElement: QuerySelectorOrElement,
        eventName: string
    ) {
        const el = this.querySelectorOrElement(querySelectorOrElement);
        const event = new Event(eventName);
        el.dispatchEvent(event);
        this.fixture.detectChanges();
    }

    /**
     * This is a helper method that can either take:
     * - A string to pass to a querySelector call on the fixture to get an element
     * - An element
     * This is used to make a shorter api for performing common operations
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     */
    private querySelectorOrElement<T extends Element>(
        querySelectorOrElement: QuerySelectorOrElement<T>
    ): T {
        if (typeof querySelectorOrElement === 'string') {
            return this.querySelector<T>(querySelectorOrElement);
        }
        return querySelectorOrElement;
    }

    /**
     * Triggers the "mousedown" event on an element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param eventName The name of the event to trigger on the element.
     */
    triggerMousedown(querySelectorOrElement: QuerySelectorOrElement) {
        this.triggerEvent(querySelectorOrElement, 'mousedown');
    }
    /**
     * Triggers the "mouseover" event on an element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param eventName The name of the event to trigger on the element.
     */
    triggerHover(querySelectorOrElement: QuerySelectorOrElement) {
        this.triggerEvent(querySelectorOrElement, 'mouseover');
    }
    /**
     * Triggers the "focus" event on an element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param eventName The name of the event to trigger on the element.
     */
    triggerFocus(querySelectorOrElement: QuerySelectorOrElement) {
        this.triggerEvent(querySelectorOrElement, 'focus');
    }
    /**
     * Triggers the "blue" event on an element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param eventName The name of the event to trigger on the element.
     */
    triggerBlur(querySelectorOrElement: QuerySelectorOrElement) {
        this.triggerEvent(querySelectorOrElement, 'blur');
    }

    /**
     * Triggers a keydown event on the given element. Updates the fixture.
     *
     * @param querySelectorOrElement Either a query selector string or an element.
     * @param key The key to press. E.g. "ArrowDown" or "Enter"
     */
    triggerKeydown(
        querySelectorOrElement: QuerySelectorOrElement,
        key: string
    ) {
        const el = this.querySelectorOrElement(querySelectorOrElement);
        const event = new KeyboardEvent('keydown', {
            key
        });
        el.dispatchEvent(event);
        this.fixture.detectChanges();
    }
}
